import Image from "next/image";
import { useTranslation } from "next-i18next";
import strftime from "strftime";
import {
  PageQuery,
  Get_SubscriptionQuery,
  SubscriptionStatus,
  BillingFrequency,
} from "gql/graphql";

import Button from "./Button/Button";
import ButtonOrLink from "components/Button/ButtonOrLink";
import Copy from "public/assets/copy.svg";
import ChevronRight from "public/assets/chevron-right.svg";
import CopyToClipboardButton from "./Button/CopyToClipboardButton";
import Explicit from "public/assets/explicit.svg";
import Listen from "components/Listen";
import Lock from "public/assets/lock.svg";
import Stripe from "public/assets/stripe.svg";

export default function SubscriptionDetails({
  subscriptionData,
  data,
  multipleShows,
  displayShowDetailsDialog,
}: {
  subscriptionData: Get_SubscriptionQuery | undefined;
  data: PageQuery;
  multipleShows: boolean;
  displayShowDetailsDialog: (show: any) => void;
}) {
  const { t } = useTranslation("common");

  return (
    <>
      <h2 className="font-display text-[32px] font-bold leading-none lg:text-[40px]">
        {t("your_content")}
      </h2>

      <p className="mt-6 text-white/70">{t("your_content_subheader")} </p>

      {!multipleShows && (
        <div className="mt-8">
          <Listen
            playbackOptions={subscriptionData!.subscription?.product.shows[0].playbackOptions}
          />
        </div>
      )}
      {multipleShows && (
        <section className="mt-11">
          <h2 className="flex items-center gap-4 text-sm font-semibold text-white lg:text-base">
            <span>{t("shows")}</span>
            <span className="h-px w-full flex-1 bg-white/[.08]"></span>
          </h2>
          <ul className="mt-6 space-y-5">
            {subscriptionData?.subscription?.product.shows.map((show, i) => (
              <li key={i}>
                <ButtonOrLink
                  onClick={() =>
                    displayShowDetailsDialog({
                      title: show.title,
                      productTitle: data?.page.hero.title,
                      art: show.coverImageUrl,
                      details: data?.page.hero.metadata.join("・"),
                      premium: true,
                      explicit: show.explicit,
                      playbackOptions: show.playbackOptions,
                    })
                  }
                  className="group flex w-full items-center gap-4 rounded focus:shadow-input-focus focus:outline-none focus:ring-0"
                >
                  <span className="h-14 w-14 shrink-0">
                    <Image
                      className="w-l4 h-14 rounded"
                      src={show.coverImageUrl}
                      width="56"
                      height="56"
                      quality={100}
                    />
                  </span>
                  <div className="flex-1 overflow-hidden">
                    <div className="flex items-center justify-between gap-4">
                      <span className="font-semibold">{show.title}</span>
                      <ChevronRight className="h-6 w-6 fill-white/50 transition group-hover:fill-white/90" />
                    </div>
                    <p className="mt-0.5 flex text-[13px] font-medium text-white/70 md:text-sm">
                      <span className="truncate">{show.subtitle}</span>
                      {show.explicit && (
                        <Explicit className="inline-block shrink-0 fill-white/70 pt-[1px] align-baseline" />
                      )}
                    </p>
                  </div>
                </ButtonOrLink>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}
